// What the alert actually says.
//
// The popup was telling an operator "Bay dwell threshold exceeded · 3 new alerts", which names the
// rule and not the lorry. Nobody has ever walked away from that message knowing what to do: the
// first question is always which truck, and the answer was a click away on another page.
//
// So the lorry comes first, because that is the thing a person can go and look at, and the rule
// second, because that is why they are being told. Where several arrive at once the shape changes
// rather than the first one winning and the rest becoming a number: three lorries doing the same
// thing is one sentence, three doing different things is a count and the worst of them.

const RANK = { CRITICAL: 3, HIGH: 2, MEDIUM: 1, LOW: 0 };

/** The most serious of a batch, since that is what the colour and the timeout follow. */
export const worstOf = (items) => items.reduce(
  (held, item) => ((RANK[item.severity] ?? 0) > (RANK[held] ?? -1) ? item.severity : held),
  'LOW',
);

/** One alert, named by the lorry it is about. */
const one = (item) => (item.vehicleReference
  ? `${item.vehicleReference} — ${item.title}`
  : item.title);

/**
 * The line to show for everything that has just come in.
 *
 * Returns the headline, a quieter second line where there is more to say, and the severity the
 * popup should wear - so the colour and the "this one does not time out" rule follow the worst
 * thing in the batch rather than whichever happened to arrive first.
 */
export const alertText = (fresh) => {
  if (!fresh || !fresh.length) return null;
  const severity = worstOf(fresh);
  if (fresh.length === 1) {
    return { headline: one(fresh[0]), detail: null, severity };
  }
  const kinds = new Set(fresh.map((item) => item.title));
  if (kinds.size === 1) {
    // The same thing happening to several lorries is one fact, not several alerts, and naming them
    // is more use than counting them - up to the point where the list stops being readable.
    const named = fresh.map((item) => item.vehicleReference).filter(Boolean);
    return {
      headline: `${fresh.length} lorries — ${fresh[0].title}`,
      detail: named.length && named.length <= 3 ? named.join(', ') : null,
      severity,
    };
  }
  // Different things at once: lead with the worst, and say how many others are waiting rather than
  // letting the first to arrive speak for all of them.
  const worst = fresh.find((item) => item.severity === severity) || fresh[0];
  return {
    headline: one(worst),
    detail: `and ${fresh.length - 1} other alert${fresh.length - 1 === 1 ? '' : 's'}`,
    severity,
  };
};

/** What the browser notification says when the tab is not in front. */
export const desktopText = (fresh, brandName) => {
  const said = alertText(fresh);
  if (!said) return null;
  return {
    title: said.headline,
    body: said.detail || `Raised in ${brandName} just now. Click to review.`,
    critical: said.severity === 'CRITICAL',
  };
};
